import { vapidPublicKeyResponseSchema } from '@mevaser/shared'
import { api } from './api.js'

/**
 * Browser side of web-push for new-ticket alerts (t1078).
 *
 * `needs-install` is iOS/iPadOS Safari in a normal tab: Apple only exposes the
 * push APIs to a web app that has been added to the Home Screen, so the
 * settings screen has to say so instead of showing a button that cannot work.
 */
export type PushSupport = 'supported' | 'needs-install' | 'unsupported'

export class PushPermissionError extends Error {
  constructor(readonly permission: NotificationPermission) {
    super(
      permission === 'denied'
        ? 'Notifications are blocked for this site in your browser settings.'
        : 'Notification permission was not granted.',
    )
    this.name = 'PushPermissionError'
  }
}

function isIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports itself as a Mac; it is the touch screen that gives it away.
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  )
}

export function isInstalledWebApp(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export function getPushSupport(): PushSupport {
  const hasApis =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  if (hasApis) return 'supported'
  return isIos() && !isInstalledWebApp() ? 'needs-install' : 'unsupported'
}

export function getNotificationPermission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported'
}

/** Registered once at startup. Failure is silent: the in-page toast and the
 *  email/text alerts all work without a service worker. */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined)
  })
}

function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padded = value + '='.repeat((4 - (value.length % 4)) % 4)
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

/** The push subscription this browser currently holds, if any. */
export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  if (getPushSupport() !== 'supported') return null
  const registration = await navigator.serviceWorker.getRegistration('/')
  return registration ? registration.pushManager.getSubscription() : null
}

/**
 * Must be called straight from a click: browsers only show the permission
 * prompt in response to a user gesture, and Safari refuses it otherwise.
 * `requestPermission` therefore has to be the first thing awaited.
 */
export async function subscribeToPush(): Promise<void> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new PushPermissionError(permission)

  await navigator.serviceWorker.register('/sw.js')
  const registration = await navigator.serviceWorker.ready
  const { publicKey } = await api.get('/me/push/vapid-public-key', {
    schema: vapidPublicKeyResponseSchema,
  })

  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }))

  // Always re-sent, even for an existing subscription: it re-attaches this
  // browser to whoever is signed in now, which matters on a shared tablet.
  const json = subscription.toJSON()
  await api.post('/me/push/subscriptions', { endpoint: json.endpoint, keys: json.keys })
}

export async function unsubscribeFromPush(): Promise<void> {
  const subscription = await getCurrentPushSubscription()
  if (!subscription) return
  await api.post('/me/push/unsubscribe', { endpoint: subscription.endpoint })
  await subscription.unsubscribe()
}
